import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  Slide,
  TextField,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { Form, Formik, FormikHelpers } from 'formik';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { ISubtask } from '../../../../models/subtask.model';
import { AppState } from '../../../../state/configureStore';
import {
  clearSubtaskItemSection,
  createSubtask,
  fetchSubtask,
  updateSubtask,
} from '../../../../state/subtask';
import { SubtaskState } from '../../../../state/subtask';
import { fetchSubtaskTypeList, SubtaskTypeState } from '../../../../state/subtaskType';
import { TaskState } from '../../../../state/task';
import { User } from '../../../../state/user';
import UserComboBox from '../../../components/UserComboBox';
import { AuthState } from '../../../../state/auth';
import { Close as CloseIcon, Save as SaveIcon } from '@material-ui/icons';
import { TransitionProps } from '@material-ui/core/transitions';
import CustomCaption from '../../../components/CustomCaption';
import { Grid as MUIGrid } from '@mui/material';
import {
  NoteAltOutlined as NoteAltIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  AccountTreeOutlined as AccountTreeOutlinedIcon,
  PersonOutline as PersonOutlineIcon,
  EventNote as EventNoteIcon,
} from '@mui/icons-material';

const SubtaskCreateSchema = Yup.object().shape({
  subject: Yup.string().required('Subject is required'),
  dueDate: Yup.string().required('Due Date is required'),
  smSubtaskAssignees: Yup.array()
    .of(
      Yup.object().shape({
        userId: Yup.number(),
      }),
    )
    .required('Assignee is required'),
});

interface SubtaskCreateDialogProps {
  open: boolean;
  handleClose: () => void;
  subtaskId: number | null;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogPaper: {
      borderRadius: 16,
      overflow: 'hidden',
      boxShadow: '0 30px 80px rgba(15, 23, 42, 0.24)',
    },
    dialogTitle: {
      padding: 0,
      background:
        'radial-gradient(circle at top right, rgba(255,255,255,0.2), transparent 30%), linear-gradient(135deg, #072577 0%, #1d4ed8 55%, #60a5fa 100%)',
    },
    titleContent: {
      padding: theme.spacing(2, 3, 1),
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      //gap: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
    },
    titleKicker: {
      fontSize: '0.72rem',
      fontWeight: 700,
      letterSpacing: '0.16em',
      textTransform: 'uppercase',
      color: 'rgba(191, 219, 254, 0.84)',
    },
    titleHeading: {
      color: '#f8fafc',
      fontWeight: 800,
      letterSpacing: '-0.03em',
      // marginTop: theme.spacing(0.9),
    },
    titleSubtitle: {
      color: 'rgba(226, 232, 240, 0.84)',
      // marginTop: theme.spacing(1),
      lineHeight: 1.7,
      maxWidth: 520,
    },
    titleBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: theme.spacing(1, 1.5),
      borderRadius: 999,
      border: '1px solid rgba(255,255,255,0.14)',
      background:
        'linear-gradient(180deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.08) 100%)',
      color: '#ffffff',
      fontSize: '0.82rem',
      fontWeight: 700,
      backdropFilter: 'blur(8px)',
    },
    formControl: {
      margin: theme.spacing(1),
      minWidth: 300,
    },
    content: {
      padding: theme.spacing(2.5, 3, 2),
      background:
        'linear-gradient(180deg, rgba(248,250,252,0.92) 0%, rgba(255,255,255,1) 100%)',
    },
    selectEmpty: {
      marginTop: theme.spacing(2),
    },
    subtaskType: {
      marginLeft: theme.spacing(0),
      // minWidth: 300,
    },
    actions: {
      padding: theme.spacing(0, 3, 2.5),
      justifyContent: 'space-between',
      alignItems: 'center',
      background:
        'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(248,250,252,0.96) 40%)',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: theme.spacing(1),
      },
    },
    actionText: {
      color: '#64748b',
      fontSize: '0.88rem',
      lineHeight: 1.6,
      maxWidth: 420,
    },
    actionShell: {
      display: 'flex',
      gap: theme.spacing(1),
      padding: theme.spacing(0.75),
      borderRadius: 999,
      border: '1px solid rgba(148, 163, 184, 0.18)',
      background:
        'linear-gradient(135deg, rgba(255,255,255,0.94) 0%, rgba(248,250,252,0.88) 100%)',
      boxShadow: '0 16px 36px rgba(15, 23, 42, 0.08)',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    secondaryButton: {
      minHeight: 44,
      padding: theme.spacing(0, 2),
      borderRadius: 14,
      textTransform: 'none',
      fontWeight: 700,
      boxShadow: 'none',
      [theme.breakpoints.down('sm')]: {
        flex: 1,
      },
    },
    primaryButton: {
      minHeight: 44,
      padding: theme.spacing(0, 2.5),
      borderRadius: 14,
      textTransform: 'none',
      fontWeight: 700,
      background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 55%, #60a5fa 100%)',
      boxShadow: '0 14px 30px rgba(37, 99, 235, 0.24)',
      '&:hover': {
        background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 55%, #3b82f6 100%)',
        boxShadow: '0 16px 34px rgba(37, 99, 235, 0.28)',
      },
      [theme.breakpoints.down('sm')]: {
        flex: 1,
      },
    },
  }),
);

interface ISubtaskCreateDialogValues {
  subject: string;
  dueDate: string;
  description: string;
  status: ISubtask['status'];
  smSubtaskTypeId: number;
  assignee?: User[];
  smSubtaskAssignees?: ISubtask['smSubtaskAssignees'];
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return (
    <Slide direction="up" ref={ref} {...props} timeout={{ enter: 400, exit: 400 }} />
  );
});

const SubtaskCreateDialog: React.FC<SubtaskCreateDialogProps> = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { open, handleClose, subtaskId } = props;
  const { item: task } = useSelector<AppState, TaskState>((state) => state.task);
  const { item: subtask } = useSelector<AppState, SubtaskState>((state) => state.subtask);
  const { items: subtaskType } = useSelector<AppState, SubtaskTypeState>(
    (state) => state.subtaskType,
  );
  const { userId } = useSelector<AppState, AuthState>((state) => state.auth);

  const initDueDate = moment(task && task.dueDate ? task.dueDate : undefined).format(
    'YYYY-MM-DD',
  );
  const {
    dueDate = initDueDate,
    subject = '',
    description = '',
    status,
    smSubtaskTypeId = 1,
    assignee,
    smSubtaskAssignees,
  } = subtask;
  const isUpdateMode = subtaskId !== null;

  const initialValues = useMemo<ISubtaskCreateDialogValues>(
    () => ({
      subject,
      dueDate,
      description,
      status,
      smSubtaskTypeId,
      assignee,
      smSubtaskAssignees,
    }),
    [
      assignee,
      description,
      dueDate,
      smSubtaskAssignees,
      smSubtaskTypeId,
      status,
      subject,
    ],
  );

  const handleSubmit = (
    values: ISubtaskCreateDialogValues,
    actions: FormikHelpers<ISubtaskCreateDialogValues>,
  ) => {
    const valueWithSmTaskId: ISubtask = {
      ...values,
      smTaskId: task.id,
      smSubtaskAssignees: values.smSubtaskAssignees,
    };

    if (isUpdateMode) {
      const updateValue = { ...valueWithSmTaskId, id: subtaskId as number };
      dispatch(updateSubtask(updateValue, handleClose));
    } else {
      const createValue = { ...valueWithSmTaskId, requesterId: userId };
      dispatch(createSubtask(createValue, handleClose));
    }
    actions.setSubmitting(false);
  };

  const handleCloseDialog = () => {
    handleClose();
    dispatch(clearSubtaskItemSection());
  };

  useEffect(() => {
    if (subtaskId) {
      dispatch(fetchSubtask(subtaskId));
    }
    dispatch(
      fetchSubtaskTypeList(
        { 'IsSpecialType.EqualsTo': 'false' },
        {
          pageNumber: 0,
          pageSize: 15,
        },
      ),
    );
  }, [dispatch, subtaskId]);

  useEffect(() => {
    if (open) {
      dispatch(
        fetchSubtaskTypeList(
          { 'IsSpecialType.EqualsTo': 'false' },
          {
            pageNumber: 0,
            pageSize: 15,
          },
        ),
      );
    }
  }, [open]);

  return (
    <Formik<ISubtaskCreateDialogValues>
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={SubtaskCreateSchema}
      onSubmit={handleSubmit}
      validateOnChange={false}
    >
      {({ values, handleChange, errors, submitForm, setFieldValue, resetForm }) => {
        return (
          <Form>
            <Dialog
              open={open}
              TransitionComponent={Transition}
              onClose={handleCloseDialog}
              onExit={() => resetForm()}
              maxWidth="md"
              fullWidth={true}
              PaperProps={{ className: classes.dialogPaper }}
            >
              <DialogTitle className={classes.dialogTitle} disableTypography>
                <Box className={classes.titleContent}>
                  <Box>
                    <Typography variant="h4" className={classes.titleHeading}>
                      {isUpdateMode ? 'Refine Subtask' : 'Create Subtask'}
                    </Typography>
                    <Typography variant="body1" className={classes.titleSubtitle}>
                      Set the brief, owner, and due date in one polished flow before
                      saving.
                    </Typography>
                  </Box>
                </Box>
              </DialogTitle>
              <DialogContent className={classes.content}>
                <MUIGrid container spacing={1}>
                  {/* Subject */}
                  <MUIGrid item xs={12}>
                    <CustomCaption
                      caption={'Subject'}
                      icon={<NoteAltIcon fontSize="small" style={{ color: '#4f46e5' }} />}
                    />
                    <TextField
                      name="subject"
                      type="input"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      value={values.subject}
                      onChange={handleChange}
                      error={!!errors.subject}
                      helperText={errors.subject}
                      placeholder="Enter subject of the subtask"
                    />
                  </MUIGrid>

                  {/* Description */}
                  <MUIGrid item xs={12}>
                    <CustomCaption
                      caption={'Description'}
                      icon={
                        <DescriptionOutlinedIcon
                          fontSize="small"
                          style={{ color: '#0891b2' }}
                        />
                      }
                    />
                    <TextField
                      name="description"
                      type="input"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      value={values.description}
                      onChange={handleChange}
                      placeholder="Provide more details about the subtask"
                    />
                  </MUIGrid>

                  {/* Subtask Type */}
                  <MUIGrid item xs={3}>
                    <CustomCaption
                      caption={'Subtask Type'}
                      icon={
                        <AccountTreeOutlinedIcon
                          fontSize="small"
                          style={{ color: '#7c3aed' }}
                        />
                      }
                    />
                    <FormControl fullWidth variant="outlined" margin="dense">
                      <Select
                        className={classes.subtaskType}
                        value={values.smSubtaskTypeId || ''}
                        onChange={handleChange}
                        inputProps={{ name: 'smSubtaskTypeId' }}
                        variant="outlined"
                      >
                        {subtaskType.map((item) => {
                          return (
                            <MenuItem key={item.id} value={item.id}>
                              {item.name}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </MUIGrid>
                  {/* Due Date */}
                  <MUIGrid item xs={3}>
                    <CustomCaption
                      caption={'Due Date'}
                      icon={
                        <EventNoteIcon fontSize="small" style={{ color: '#ef4444' }} />
                      }
                    />
                    <KeyboardDatePicker
                      fullWidth
                      margin="dense"
                      id="date-picker-dialog"
                      format="yyyy-MM-dd"
                      value={values.dueDate}
                      onChange={(selectedDate, selectedStringDate) => {
                        setFieldValue('dueDate', selectedStringDate);
                      }}
                      inputVariant="outlined"
                    />
                  </MUIGrid>

                  {/* Assignee */}
                  <MUIGrid item xs={6}>
                    <CustomCaption
                      caption={'Assignee'}
                      icon={
                        <PersonOutlineIcon
                          fontSize="small"
                          style={{ color: '#f59e0b' }}
                        />
                      }
                    />
                    <UserComboBox
                      handleChange={(users: User[]) => {
                        setFieldValue('assignee', users ? users : []);
                        setFieldValue(
                          'smSubtaskAssignees',
                          (users
                            ? users.map((user) => ({ userId: user.id }))
                            : []) as ISubtask['smSubtaskAssignees'],
                        );
                      }}
                      selectedValue={(values.assignee as User[]) || []}
                      error={!!errors.smSubtaskAssignees}
                      helperText={errors.smSubtaskAssignees}
                      variant="outlined"
                      isMulti
                    />
                  </MUIGrid>
                </MUIGrid>
              </DialogContent>
              <DialogActions className={classes.actions}>
                <Typography className={classes.actionText}>
                  Keep the subtask concise and assign only the teammates who will move it
                  forward.
                </Typography>
                <Box>
                  <Button
                    onClick={handleCloseDialog}
                    color="primary"
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    className={classes.secondaryButton}
                  >
                    Close
                  </Button>
                  <Button
                    onClick={() => {
                      submitForm();
                    }}
                    color="primary"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    className={classes.primaryButton}
                  >
                    Save
                  </Button>
                </Box>
              </DialogActions>
            </Dialog>
          </Form>
        );
      }}
    </Formik>
  );
};

export default SubtaskCreateDialog;
